/**
 * Shared column sizing for the uniform module's tables.
 *
 * Why this exists: a uniform's name is free text and routinely long ("Chef
 * Shirt Long Sleeve — Female"), so an unsized column takes whatever the
 * longest value in the page asks for and pushes every other column off the
 * side. Two files had already sized it by hand and disagreed on the number;
 * this is the single answer.
 *
 * The rule for every table in this module: a text column whose content is
 * user-supplied gets a width AND truncation with the full value on hover.
 * Never let content decide the width.
 */

/**
 * Width for a uniform-name column, pinned or not.
 *
 * 150 fits "Chef Shirt" and most two-word names outright, and truncates the
 * rest — which is the trade: the name is an identifier the reader scans, not
 * something they read in full, and the hover gives them the whole thing when
 * they need it.
 */
export const UNIFORM_LANE = 150;

/**
 * Truncate a plain text column and put the full value in a `title`.
 *
 * antd's own `ellipsis: { showTitle: true }` does the work; it is spelled
 * out here so every caller gets the title attribute rather than the bare
 * `ellipsis: true`, which truncates silently and leaves no way to read what
 * was cut.
 *
 * Only for columns that render plain text. `ellipsis` styles the CELL, which
 * does nothing when the cell's child is a block or flex container that sizes
 * itself — those need `ELLIPSIS` on the inner element instead.
 */
export const truncatedText = (width) => ({ width, ellipsis: { showTitle: true } });

/**
 * Truncation for a custom-rendered cell, applied to the inner element.
 *
 * Pair it with a `title` carrying the full value, the way the bulk issuance
 * grid does — without one, the value is simply lost.
 */
export const ELLIPSIS = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

/** A ready-made uniform-name column. Spread `pinLeft(...)` over it to pin. */
export const uniformNameColumn = (overrides = {}) => ({
  title: 'Uniform',
  dataIndex: 'uniform_item_name',
  ...truncatedText(UNIFORM_LANE),
  ...overrides,
});
